import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { api } from '@/runtime'
import type { AgentStats } from '@/db/api'
import { useAllRuns } from '@/db/hooks'
import { AgentsLayout } from '@/components/AgentList'
import { UsagePanel } from '@/components/UsagePanel'
import { Skeleton } from '@/components/Skeleton'
import { useRunStatus } from '@/components/RunStatus'
import { Failed, IconButton, SectionTitle, Segmented, Tag } from '@/components/ui'
import { fmtDateShort, parseDate, todayLocal } from '@/lib/time'

// What Prime and the coding agents spent and did (/agents/stats): spend (the
// growth of each session's cost, recorded every minute by the factory watcher),
// turns, agents started, pull requests opened and merged, a bar a day split
// between Prime and the agents, the sessions that cost most, and the quick
// chat's own token ledger. Online only.

// the day's initial under its bar (local calendar dates, never converted)
const weekday = (date: string) => parseDate(date).toLocaleDateString(undefined, { weekday: 'narrow' })

const usd = (n: number) => (n >= 100 ? `$${Math.round(n)}` : `$${n.toFixed(2)}`)

const Tile = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="card px-3 py-2.5" data-testid="stat-tile">
    <p className="text-xs text-muted">{label}</p>
    <p className="t-title2 tabular-nums">{value}</p>
    {hint && <p className="text-[11px] text-faint">{hint}</p>}
  </div>
)

/** The page's one period, and what the quick chat's ledger calls it. */
const WINDOWS = [
  { value: 1, label: 'Today', ledger: 'today' },
  { value: 7, label: '7 days', ledger: 'week' },
  { value: 30, label: '30 days', ledger: 'month' },
] as const

export const AgentStatsView = () => {
  const navigate = useNavigate()
  const runs = useAllRuns()
  const statusOf = useRunStatus()
  const [days, setDays] = useState<1 | 7 | 30>(7)
  const [data, setData] = useState<{ days: number; stats: AgentStats } | null>(null)
  const [error, setError] = useState('')
  const [nudge, setNudge] = useState(0)
  useEffect(() => {
    let stale = false
    api
      .agentStats(days)
      .then((s) => {
        if (!stale) {
          setData({ days, stats: s })
          setError('')
        }
      })
      .catch((e: Error) => {
        if (!stale) setError(e.message)
      })
    return () => {
      stale = true
    }
  }, [days, nudge])
  const stats = data?.days === days ? data.stats : null
  const peak = Math.max(0.01, ...(stats?.by_day ?? []).map((d) => d.lead_usd + d.agents_usd))
  const today = todayLocal()
  return (
    <AgentsLayout currentId="stats">
      <div data-testid="agent-stats">
        {/* the agent pages' top bar: back to every agent on a phone, the title, and the one period for the page */}
        <header
          className="glass-bar sticky top-0 z-20 -mx-3 px-3 pb-2 mb-4 bg-ink border-b border-line/60"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)',
            marginTop: 'calc(-1 * (env(safe-area-inset-top) + 0.5rem))',
          }}
          data-testid="stats-topbar"
        >
          <div className="flex items-center gap-1 min-h-11">
            <span className="lg:hidden -ml-2">
              <IconButton label="All agents" onClick={() => void navigate({ to: '/agents/all' })}>
                <ChevronLeft size={20} aria-hidden />
              </IconButton>
            </span>
            <h1 className="flex-1 min-w-0 text-[17px] font-semibold tracking-tight leading-snug truncate">
              Agent stats
            </h1>
            <Segmented
              label="Period"
              value={days}
              options={WINDOWS.map((w) => ({ value: w.value, label: w.label }))}
              onChange={setDays}
            />
          </div>
        </header>
        {error && !stats && (
          <Failed
            what="the agents' stats"
            reason={error}
            onRetry={() => setNudge((n) => n + 1)}
            className="mb-3"
          />
        )}
        {!stats && !error && (
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        )}
        {stats && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Tile
                label="Spent"
                value={usd(stats.totals.usd)}
                hint={stats.notional ? 'API-equivalent; your subscription pays' : undefined}
              />
              <Tile label="Prime" value={usd(stats.totals.lead_usd)} />
              <Tile label="Coding agents" value={usd(stats.totals.agents_usd)} />
              <Tile label="Turns" value={String(stats.totals.turns)} />
              <Tile label="Agents started" value={String(stats.totals.started)} />
              <Tile label="Pull requests opened" value={String(stats.totals.prs_opened)} />
              <Tile label="Merged" value={String(stats.totals.prs_merged)} />
            </div>

            {stats.by_day.length > 1 && (
              <section className="mt-6">
                <SectionTitle>A day at a time</SectionTitle>
                <div className="flex items-end gap-1 h-32" data-testid="stats-days">
                  {stats.by_day.map((d) => {
                    const total = d.lead_usd + d.agents_usd
                    return (
                      <div
                        key={d.date}
                        className="flex-1 min-w-0 flex flex-col justify-end h-full"
                        title={`${d.date === today ? 'Today' : fmtDateShort(d.date)}: ${usd(total)}, ${d.turns} turns`}
                      >
                        <div
                          className="bg-accent rounded-t-[3px]"
                          style={{ height: `${(d.agents_usd / peak) * 100}%` }}
                        />
                        <div
                          className="bg-fg/35"
                          style={{
                            height: `${(d.lead_usd / peak) * 100}%`,
                            borderTopLeftRadius: d.agents_usd ? 0 : 3,
                            borderTopRightRadius: d.agents_usd ? 0 : 3,
                          }}
                        />
                      </div>
                    )
                  })}
                </div>
                {stats.by_day.length <= 14 && (
                  <div className="flex gap-1 mt-1" aria-hidden>
                    {stats.by_day.map((d) => (
                      <span key={d.date} className="flex-1 min-w-0 text-center text-[10px] text-faint">
                        {weekday(d.date)}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-faint mt-1.5 flex gap-3">
                  <span className="flex items-center gap-1">
                    <span className="inline-block size-2 rounded-sm bg-accent" aria-hidden /> Coding agents
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block size-2 rounded-sm bg-fg/35" aria-hidden /> Prime
                  </span>
                </p>
              </section>
            )}

            {stats.top.length > 0 && (
              <section className="mt-6">
                <SectionTitle>What cost most</SectionTitle>
                <ul className="group-list" data-testid="stats-top">
                  {stats.top.map((t) => {
                    const run = runs?.find((r) => r.id === t.id)
                    // the agent's chip from the one model (lib/waiting.ts), as everywhere else
                    const st = run && !t.lead ? statusOf(run) : undefined
                    return (
                      <li key={t.id}>
                        <Link
                          to="/agents/run/$id"
                          params={{ id: t.id }}
                          className="flex items-center gap-2 px-3 py-2 min-h-11 text-sm"
                        >
                          <span className="flex-1 min-w-0 truncate">{t.lead ? 'Prime' : t.title}</span>
                          {t.pr ? <Tag>PR #{t.pr}</Tag> : null}
                          {st && <Tag tone={st.tone}>{st.word}</Tag>}
                          <span className="tabular-nums text-muted w-14 text-right">{usd(t.usd)}</span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </section>
            )}
          </>
        )}

        <section className="mt-6 mb-10">
          <SectionTitle>Quick chat and voice</SectionTitle>
          {/* the page's own period picker drives it: one picker on the page */}
          <UsagePanel period={WINDOWS.find((w) => w.value === days)?.ledger ?? 'week'} />
        </section>
      </div>
    </AgentsLayout>
  )
}
